import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { unwrapResult } from '@reduxjs/toolkit'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import { useAppDispatch } from 'store/hooks'
import { offeringUpdateThunk } from 'store/reducers/temple/offeringSlice'
import { IOfferingResponse } from 'types/api/temple'
import { extractError } from 'utils/api'
import PoolSelect from './PoolSelect'

interface BulkOfferingPoolDialogProps {
    open: boolean
    offerings: IOfferingResponse[]
    onClose: () => void
    onComplete?: () => void
}

const BulkOfferingPoolDialog: React.FC<BulkOfferingPoolDialogProps> = ({ open, offerings, onClose, onComplete }) => {
    const dispatch = useAppDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const [selectedPools, setSelectedPools] = useState<string[]>([])
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        if (open) {
            // Optional: Initialize with pools from the first offering, or leave empty
            setSelectedPools([])
        }
    }, [open, offerings])

    const handleSave = async () => {
        if (offerings.length === 0) return

        setSubmitting(true)
        try {
            await Promise.all(
                offerings.map(offering =>
                    dispatch(offeringUpdateThunk({
                        url: offering.url,
                        data: { poolConnections: selectedPools.map(poolUrl => ({ pool: poolUrl })) }
                    })).then(unwrapResult)
                )
            )
            enqueueSnackbar('Updated pool for selected offerings', { variant: 'success' })
            onComplete?.()
            onClose()
        } catch (error) {
            enqueueSnackbar(extractError(error) ?? 'Failed to update selected offerings', { variant: 'error' })
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Bulk Assign Pool</DialogTitle>
            <DialogContent>
                <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                        Apply one pool to {offerings.length} selected offering{offerings.length === 1 ? '' : 's'}. This will overwrite their existing root pool connection.
                    </Typography>
                </Box>
                <Box display="flex" flexDirection="column">
                    <PoolSelect pools={selectedPools} setPools={setSelectedPools} />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" variant="outlined" disabled={submitting}>
                    Cancel
                </Button>
                <Button onClick={handleSave} color="primary" variant="contained" disabled={submitting || offerings.length === 0}>
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default BulkOfferingPoolDialog
